import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { Button, Col, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import React, { useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import * as Yup from 'yup';

import { errorHandler } from 'App/utils';
import Conditions from './Conditions';
import MedicationSelect from '../MedicationSelect/MedicationSelect';
import FindPatientTable from 'App/components/FindPatient/FindPatientTable';
import CategorySelect from 'App/components/CategorySelect';
import GenderSelect from 'App/components/FindPatient/GenderSelect';
import BranchSelect from 'App/components/FindPatient/BranchSelect';
import { isoDateOnlyFormat } from 'App/services/DateService';
import delay from 'App/utils/delay';
import { TextField } from 'Shared/form/TextField';
import { CheckboxField } from 'Shared/form/CheckboxField';
import UpdateSearchParams from 'App/components/FindPatient/UpdateSearchParams';
import PESService from '../../services/PESService';
import { DatePickerField } from 'Shared/form/DatePickerField';
import EhrTypeSelect from 'App/components/FindPatient/EhrTypeSelect';
import PatientLabels from '../AddPatient/Components/PatientLabels';
import PatientStatus from '../AddPatient/Components/PatientStatus';
import { PhoneField } from '../PatientForm/components/PhoneField';
import useGetCustomMenuQuery from 'App/hooks/useGetCustomMenuQuery';
import SelectField from 'Shared/form/SelectField';
import HighRisk from '../PatientMessaging/HighRisk';
import HighRiskReasons from '../PatientMessaging/HighRiskReasons';
import SubmitButton from 'Shared/form/SubmitButton';
import AccordionWrapper from 'App/components/AccordionWrapper/AccordionWrapper';
import './FindPatientForm.scss';
import useIsMounted from 'App/hooks/useIsMounted';

const propTypes = {
    paramFirstName: PropTypes.string,
    paramLastName: PropTypes.string,
    paramId: PropTypes.string,
    paramPhoneNumber: PropTypes.string,
};

const defaultProps = {
    paramFirstName: '',
    paramLastName: '',
    paramId: '',
    paramPhoneNumber: '',
};
const PaddedRow = styled(Row)`
    padding: 0.5% 0;
`;

export const initialSearchParams = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    birthDate: null,
    gender: null,
    city: '',
    state: '',
    ssnLastFour: '',
    email: '',
    zip: '',
    externalId: '',
    otherId: '',
    ehrId: '',
    ehrPrimaryIdType: null,
    internalId: '',
    category: null,
    condition: null,
    medication: null,
    branch: null,
    showDisabled: false,
    sortKey: 'lastName',
    sortOrder: 'asc',
    count: 10,
    page: 0,
};

const initialValues = {
    searchParams: initialSearchParams,
    isSortOrPageChanged: false,
    lastSubmitSearchParam: {}, //store last submit param to use in case of pagination or sorting
    data: {
        results: [],
        total: 0,
    },
};

const schema = Yup.object().shape({
    searchParams: Yup.object().shape(
        {
            ehrId: Yup.string().when('ehrPrimaryIdType', {
                is: R.identity,
                then: Yup.string().required('EHR ID is required when EHR Type is selected.'),
                otherwise: Yup.string(),
            }),
            ehrPrimaryIdType: Yup.object()
                .nullable()
                .when('ehrId', {
                    is: R.identity,
                    then: Yup.object().required('EHR Type is required when EHR ID is populated.'),
                    otherwise: Yup.object().nullable(),
                }),
            birthDate: Yup.date()
                .nullable()
                .max(new Date(9999, 11, 31), 'Date cannot be later than 9999/12/31'),
        },
        ['ehrPrimaryIdType', 'ehrId']
    ),
});

const FindPatientForm = (props) => {
    const { paramFirstName, paramLastName, paramId, paramPhoneNumber } = props; // This is the value entered by the user in the search header
    const { data: genderIdentities, isLoading: isGenderIdentitiesLoading } = useGetCustomMenuQuery('gender_identity');
    const [isSearchOpen, setIsSearchOpen] = useState(true);
    const isMounted = useIsMounted();

    return (
        <div data-testid="findPatientForm">
            <Formik
                validationSchema={schema}
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting, setFieldValue }) => {
                    let params = { ...values.lastSubmitSearchParam };
                    if (!values.isSortOrPageChanged) {
                        setIsSearchOpen(false);
                        params = R.compose(
                            R.over(R.lensProp('ehrPrimaryIdType'), R.propOr(null, 'keyValue')),
                            R.over(R.lensProp('branch'), R.propOr(null, 'id')),
                            R.over(R.lensProp('gender'), R.propOr(null, 'id')),
                            R.over(R.lensProp('category'), R.propOr(null, 'id')),
                            R.over(R.lensProp('condition'), R.propOr(null, 'id')),
                            R.over(R.lensProp('medication'), R.propOr(null, 'id')),
                            R.over(R.lensProp('page'), () => 0),
                            R.over(
                                R.lensProp('birthDate'),
                                R.ifElse(R.isNil, R.identity, (date) => dayjs(date).format(isoDateOnlyFormat))
                            ),
                            R.over(R.lensProp('genderIdentity'), R.propOr(null, 'value'))
                        )(values.searchParams);
                        params.additionalDisplayFields = ['branch'];
                        setFieldValue('lastSubmitSearchParam', params);
                        setFieldValue('searchParams.page', 0);
                        setFieldValue('lastSubmitSearchParam.page', 0);
                    }
                    setFieldValue('isSortOrPageChanged', false);
                    return Promise.all([
                        PESService.findPatients(params),
                        /**
                         * Build in a small amount of delay so if the response is really fast, we don't get a flicker
                         * from the load spinner being added and removed really fast.
                         */
                        delay(300),
                    ])
                        .then(([results]) => {
                            if (isMounted()) {
                                setFieldValue('data', results);
                            }
                        })
                        .catch(errorHandler)
                        .finally(() => {
                            if (isMounted()) {
                                setSubmitting(false);
                            }
                        });
                }}
            >
                {({ isSubmitting, setFieldValue, submitForm, handleSubmit, values, resetForm, submitCount }) => {
                    const handleSortChange = (sort) => {
                        setFieldValue('searchParams.sortKey', sort.sortKey);
                        setFieldValue('searchParams.sortOrder', sort.sortOrder);
                        setFieldValue('lastSubmitSearchParam.sortKey', sort.sortKey);
                        setFieldValue('lastSubmitSearchParam.sortOrder', sort.sortOrder);
                        setFieldValue('isSortOrPageChanged', true);

                        submitForm().then().catch(errorHandler);
                    };

                    const handlePageChange = (page) => {
                        setFieldValue('searchParams.count', page.pageSize);
                        setFieldValue('searchParams.page', page.pageIndex - 1);
                        setFieldValue('lastSubmitSearchParam.count', page.pageSize);
                        setFieldValue('lastSubmitSearchParam.page', page.pageIndex - 1);
                        setFieldValue('isSortOrPageChanged', true);

                        submitForm().then().catch(errorHandler);
                    };

                    return (
                        <>
                            <UpdateSearchParams
                                paramFirstName={paramFirstName}
                                paramLastName={paramLastName}
                                paramId={paramId}
                                paramPhoneNumber={paramPhoneNumber}
                            />
                            <form onSubmit={handleSubmit} className="find-patient-form">
                                <AccordionWrapper
                                    title="Search"
                                    controlledVisible={isSearchOpen}
                                    onClick={setIsSearchOpen}
                                    headerClassName="find-patient-accordion-header"
                                >
                                    {/*Name, DoB, Gender*/}
                                    <PaddedRow>
                                        <Col md={3}>
                                            <TextField
                                                fieldName="searchParams.lastName"
                                                label="Last Name"
                                                placeholder="Last Name"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <TextField
                                                fieldName="searchParams.firstName"
                                                label="First Name"
                                                placeholder="First Name"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <DatePickerField fieldName="searchParams.birthDate" label="Birth Date" />
                                        </Col>
                                        <Col md={3}>
                                            <GenderSelect label="Sex" />
                                        </Col>
                                    </PaddedRow>

                                    {/* Preferred Name, Patient Labels, Patient Status, Gender Identity */}
                                    <PaddedRow>
                                        <Col md={3}>
                                            <TextField
                                                fieldName="searchParams.preferredName"
                                                label="Preferred Name"
                                                placeholder="Preferred Name"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <PatientLabels
                                                value={values.searchParams.patientLabels}
                                                onChange={(patientLabels) => {
                                                    setFieldValue('searchParams.patientLabels', patientLabels);
                                                }}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <PatientStatus
                                                value={values.searchParams.patientStatus}
                                                onChange={(patientStatus) => {
                                                    setFieldValue('searchParams.patientStatus', patientStatus);
                                                }}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <SelectField
                                                label="Gender Identity"
                                                fieldName="searchParams.genderIdentity"
                                                options={genderIdentities || []}
                                                isLoading={isGenderIdentitiesLoading}
                                            />
                                        </Col>
                                    </PaddedRow>

                                    {/*City, State, Phone, Email*/}
                                    <PaddedRow>
                                        <Col md={3}>
                                            <TextField fieldName="searchParams.city" label="City" placeholder="City" />
                                        </Col>
                                        <Col md={3}>
                                            <TextField
                                                fieldName="searchParams.state"
                                                label="State"
                                                placeholder="State"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <PhoneField fieldName="searchParams.phoneNumber" label="Patient Phone" />
                                        </Col>
                                        <Col md={3}>
                                            <TextField
                                                fieldName="searchParams.email"
                                                label="Email"
                                                placeholder="Email"
                                            />
                                        </Col>
                                    </PaddedRow>
                                    {/*Zip, High Risk, High Risk Reasons and SSN*/}
                                    <PaddedRow>
                                        <Col md={3}>
                                            <TextField fieldName="searchParams.zip" label="Zip" placeholder="Zip" />
                                        </Col>
                                        <Col md={3}>
                                            <HighRisk
                                                highRiskvalue={values.searchParams.highRiskvalue}
                                                onChange={(value) => {
                                                    setFieldValue('searchParams.highRiskvalue', value);
                                                }}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <HighRiskReasons
                                                highRiskvalue={values.searchParams.highRiskvalue}
                                                onChange={(value) => {
                                                    setFieldValue('searchParams.highRiskReasonvalue', value);
                                                }}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <TextField
                                                fieldName="searchParams.ssnLastFour"
                                                label="SSN Last Four"
                                                placeholder="SSN Last Four"
                                            />
                                        </Col>
                                    </PaddedRow>

                                    {/*Branches Other ID and Human ID */}
                                    <PaddedRow>
                                        <Col md={6}>
                                            <BranchSelect
                                                onChange={(option) => {
                                                    setFieldValue('searchParams.branch', option);
                                                }}
                                                value={values.searchParams.branch}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <TextField
                                                fieldName="searchParams.otherId"
                                                label="Other ID"
                                                placeholder="Other ID"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <TextField
                                                fieldName="searchParams.internalId"
                                                label="Human ID"
                                                placeholder="Human ID"
                                            />
                                        </Col>
                                    </PaddedRow>

                                    {/*TC, Dispensing ID and EHR ID*/}
                                    <PaddedRow>
                                        <Col md={6}>
                                            <CategorySelect
                                                fieldName="searchParams.category"
                                                onChange={(option) => {
                                                    setFieldValue('searchParams.medication', null);
                                                    setFieldValue('searchParams.condition', null);
                                                    setFieldValue('searchParams.category', option);
                                                }}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <TextField
                                                fieldName="searchParams.externalId"
                                                label="Dispensing ID"
                                                placeholder="Dispensing ID"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <TextField
                                                fieldName="searchParams.ehrId"
                                                label="EHR ID"
                                                placeholder="EHR ID"
                                            />
                                        </Col>
                                    </PaddedRow>

                                    {/* Conditions, Medications and EHR type */}
                                    <PaddedRow>
                                        <Col md={6}>
                                            {values.searchParams.category ? (
                                                <Conditions
                                                    therapyId={values.searchParams.category.id}
                                                    onChange={(condition) => {
                                                        setFieldValue('searchParams.condition', condition);
                                                        setFieldValue('searchParams.medication', null);
                                                    }}
                                                    value={values.searchParams.condition}
                                                />
                                            ) : null}
                                        </Col>

                                        <Col md={3}>
                                            <EhrTypeSelect
                                                onChange={(option) => {
                                                    setFieldValue('searchParams.ehrPrimaryIdType', option);
                                                }}
                                            />
                                        </Col>
                                    </PaddedRow>

                                    <PaddedRow>
                                        {values.searchParams.category ? (
                                            <Col md={6}>
                                                <MedicationSelect
                                                    fieldName="searchParams.medication"
                                                    therapyId={values.searchParams.category.id}
                                                    onChange={(medication) => {
                                                        setFieldValue('searchParams.medication', medication);
                                                    }}
                                                />
                                            </Col>
                                        ) : null}
                                    </PaddedRow>

                                    {/*Show Disabled / Buttons*/}
                                    <PaddedRow>
                                        <Col md={6}>
                                            <CheckboxField
                                                fieldName="searchParams.showDisabled"
                                                label="Show Disabled Records"
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <div className="form-button-group">
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    disabled={isSubmitting}
                                                    onClick={() => {
                                                        setIsSearchOpen(true);
                                                        resetForm();
                                                    }}
                                                >
                                                    Reset
                                                </Button>
                                                <SubmitButton ignoreDirty>Search</SubmitButton>
                                            </div>
                                        </Col>
                                    </PaddedRow>
                                </AccordionWrapper>

                                <PaddedRow>
                                    <Col md={12}>
                                        <h3 className="section-title">Search Results</h3>
                                    </Col>
                                    {submitCount < 1 && (
                                        <Col md={12}>
                                            <FaInfoCircle style={{ marginRight: 5 }} />
                                            <span>
                                                Search results based on the search criteria above will display here.
                                            </span>
                                        </Col>
                                    )}
                                </PaddedRow>
                            </form>

                            {submitCount > 0 ? (
                                <FindPatientTable
                                    data={values.data?.results ?? []}
                                    totalRows={values.data?.total ?? 0}
                                    onPageChange={handlePageChange}
                                    onSortChange={handleSortChange}
                                    currentPage={values.searchParams.page + 1}
                                />
                            ) : null}
                        </>
                    );
                }}
            </Formik>
        </div>
    );
};

FindPatientForm.propTypes = propTypes;
FindPatientForm.defaultProps = defaultProps;

export default FindPatientForm;
